/* Persona contact router — Paper 01.G05 · Contact, D03 + D04 + States & Logic.
   A routing card picks the topic; only that topic's fields show, validate
   and submit. Links to #contact-route-<route> anywhere on the page select a
   route. Validation runs on blur and clears the moment the value is valid;
   a failed submit focuses the first errored field — no summary banner. */
(() => {
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const ALIAS = { 'existing-order': 'existing', order: 'existing', 'product-help': 'product', sizing: 'product', 'teams-clubs': 'teams', 'something-else': 'else' };
  const KEY_ROUTE = 'ps-cr-route';
  const KEY_STATE = 'ps-cr-state';
  const KEY_GONE = 'ps-cr-sticky-gone';
  const mobile = window.matchMedia('(max-width: 989px)');
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const store = {
    get(k) { try { return JSON.parse(sessionStorage.getItem(k)); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } },
    del(k) { try { sessionStorage.removeItem(k); } catch (e) { /* private mode */ } },
  };
  const norm = (r) => ALIAS[r] || r;

  class Router {
    constructor(root) {
      this.root = root;
      this.cards = [...root.querySelectorAll('[data-route]')];
      this.flow = root.querySelector('[data-ps-cr-flow]');
      this.form = root.querySelector('.ps-cr__form');
      this.sticky = root.querySelector('[data-ps-cr-sticky]');
      this.route = '';
      root.classList.add('is-js');

      this.cards.forEach((c) => c.addEventListener('click', () => this.select(c.dataset.route, true)));
      root.querySelector('[data-ps-cr-change]')?.addEventListener('click', () => this.clear());
      document.addEventListener('click', (e) => {
        const a = e.target.closest('a[href*="#contact-route-"]');
        if (!a) return;
        const url = new URL(a.href, location.href);
        if (url.pathname !== location.pathname) return;
        e.preventDefault();
        this.select(norm(url.hash.replace('#contact-route-', '')), true);
      });

      if (this.form) {
        this.fields = [...this.form.querySelectorAll('[data-routes]')];
        this.form.querySelectorAll('.ps-cr__input').forEach((i) => {
          i.addEventListener('blur', () => { if (!i.disabled) this.check(i.closest('.ps-cr__field')); });
          i.addEventListener('input', () => {
            this.goneSticky();
            const f = i.closest('.ps-cr__field');
            if (f.classList.contains('is-error')) this.check(f);
          });
        });
        this.form.addEventListener('change', (e) => {
          const f = e.target.closest('.ps-cr__field');
          if (f && f.classList.contains('is-error')) this.check(f);
        });
        this.form.addEventListener('submit', (e) => this.onSubmit(e));
        mobile.addEventListener('change', () => this.placeholders());
      }

      const done = root.querySelector('[data-done]');
      const fail = root.querySelector('[data-ps-cr-fail]');
      if (done) {
        const r = store.get(KEY_ROUTE) || 'else';
        store.del(KEY_ROUTE);
        store.del(KEY_STATE);
        this.select(r, false);
        const card = root.querySelector(`[data-done="${r}"]`) || root.querySelector('[data-done="else"]') || done;
        card.hidden = false;
        this.flow.scrollIntoView({ block: 'start' });
        card.focus({ preventScroll: true });
      } else if (fail) {
        this.select(store.get(KEY_ROUTE) || 'else', false);
        this.restore();
        this.flow.scrollIntoView({ block: 'start' });
        fail.focus({ preventScroll: true });
      } else {
        const q = new URLSearchParams(location.search).get('topic');
        const h = location.hash.startsWith('#contact-route-') ? location.hash.replace('#contact-route-', '') : '';
        const r = norm(q || h || '');
        if (r && this.cards.some((c) => c.dataset.route === r)) this.select(r, !!h);
      }
      this.initSticky();
    }

    select(route, scroll) {
      const card = this.cards.find((c) => c.dataset.route === route);
      if (!card) return;
      this.route = route;
      this.goneSticky();
      this.cards.forEach((c) => c.setAttribute('aria-pressed', c === card ? 'true' : 'false'));
      this.root.classList.add('has-route');
      this.flow.hidden = false;
      this.flow.classList.toggle('is-teams', route === 'teams');
      this.root.querySelector('[data-ps-cr-chip]').textContent = card.dataset.label;
      this.root.querySelectorAll('[data-for]').forEach((el) => { el.hidden = el.dataset.for !== route; });
      if (this.form && !this.form.querySelector('[data-done]')) {
        this.form.hidden = route === 'teams';
        this.fields.forEach((f) => {
          const on = f.dataset.routes.split(' ').includes(route);
          f.hidden = !on;
          f.querySelectorAll('input, textarea').forEach((i) => { i.disabled = !on; });
          if (!on) this.setError(f, '');
        });
        const topic = this.form.querySelector('[data-ps-cr-topic]');
        if (topic) topic.value = card.dataset.topic;
        this.placeholders();
      }
      const url = new URL(location.href);
      url.searchParams.set('topic', route);
      url.hash = '';
      history.replaceState(history.state, '', url);
      if (scroll) {
        const target = mobile.matches ? this.flow : this.flow;
        requestAnimationFrame(() => target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' }));
      }
    }

    clear() {
      this.route = '';
      this.cards.forEach((c) => c.setAttribute('aria-pressed', 'false'));
      this.root.classList.remove('has-route');
      this.flow.hidden = true;
      const url = new URL(location.href);
      url.searchParams.delete('topic');
      history.replaceState(history.state, '', url);
      const pick = this.root.querySelector('.ps-cr__pick');
      pick.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
      this.cards[0]?.focus({ preventScroll: true });
    }

    placeholders() {
      this.form.querySelectorAll('[data-ph-m]').forEach((i) => {
        if (!i.dataset.phD) i.dataset.phD = i.placeholder;
        i.placeholder = mobile.matches && i.dataset.phM ? i.dataset.phM : i.dataset.phD;
      });
    }

    setError(field, msg) {
      const err = field.querySelector('.ps-cr__err');
      field.classList.toggle('is-error', !!msg);
      field.querySelectorAll('input, textarea').forEach((i) => i.setAttribute('aria-invalid', msg ? 'true' : 'false'));
      if (!err) return;
      err.textContent = msg;
      err.hidden = !msg;
    }

    check(field) {
      const kind = field.dataset.req;
      if (!kind || field.hidden) return true;
      const d = this.root.dataset;
      let msg = '';
      if (kind === 'choice') {
        if (!field.querySelector('input:checked')) msg = d.errChoice;
      } else {
        const i = field.querySelector('.ps-cr__input');
        const v = i.value.trim();
        if (kind === 'email') msg = !v ? d.errRequired.replace('[field]', 'email') : (EMAIL.test(v) ? '' : d.errEmail);
        else if (kind === 'order') msg = v ? '' : d.errOrder;
        else msg = v ? '' : d.errRequired.replace('[field]', field.dataset.name || 'details');
      }
      this.setError(field, msg);
      return !msg;
    }

    onSubmit(e) {
      if (!this.route || this.route === 'teams') { e.preventDefault(); return; }
      const bad = this.fields.filter((f) => !f.hidden && !this.check(f));
      if (bad.length) {
        e.preventDefault();
        bad[0].scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
        const first = bad[0].querySelector('input:not([type=radio]), textarea, input[type=radio]');
        first?.focus({ preventScroll: true });
        return;
      }
      store.set(KEY_ROUTE, this.route);
      const state = {};
      new FormData(this.form).forEach((v, k) => { if (k.startsWith('contact[')) state[k] = v; });
      store.set(KEY_STATE, state);
    }

    restore() {
      const state = store.get(KEY_STATE);
      if (!state) return;
      Object.entries(state).forEach(([k, v]) => {
        this.form.querySelectorAll(`[name="${CSS.escape(k)}"]:not([disabled])`).forEach((el) => {
          if (el.type === 'radio') el.checked = el.value === v;
          else if (el.type !== 'hidden') el.value = v;
        });
      });
    }

    goneSticky() {
      store.set(KEY_GONE, true);
      if (this.updateSticky) this.updateSticky();
    }

    initSticky() {
      if (!this.sticky || !('IntersectionObserver' in window)) return;
      let pickIn = false;
      const update = () => {
        const on = !this.route && !pickIn && !store.get(KEY_GONE);
        this.sticky.classList.toggle('is-on', on);
        this.sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
        this.sticky.querySelector('a').tabIndex = on ? 0 : -1;
      };
      this.updateSticky = update;
      new IntersectionObserver(([en]) => { pickIn = en.isIntersecting; update(); }, { rootMargin: '0px 0px -40% 0px' })
        .observe(this.root.querySelector('.ps-cr__cards'));
      this.sticky.querySelector('a').addEventListener('click', (e) => {
        e.preventDefault();
        this.root.querySelector('.ps-cr__pick').scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
        this.cards[0]?.focus({ preventScroll: true });
      });
      update();
    }
  }

  const boot = () => document.querySelectorAll('[data-ps-cr]').forEach((el) => {
    if (!el.psCr) el.psCr = new Router(el);
  });
  boot();
  document.addEventListener('shopify:section:load', boot);
})();
